// Passo 5 (Revisão), tela de progresso/sucesso/falha da publicação e lista de rascunhos.
import type { ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Check, CheckCircle2, Clock, Info, RefreshCw, Sparkles, Trash2, XCircle } from 'lucide-react'
import { brl, dateBR, num } from '@impulso/shared'
import {
  GOAL_LABEL, PLATFORM_LABEL, type BlueprintDto, type DraftDto, type EstimateResponse,
  type PlatformPublishState, type ValidateResponse, type ValidationCheck,
} from '@impulso/shared/api/builder'
import { errorMessage } from '../../lib/api'
import { paymentGuideFor } from '../connections/paymentGuides'
import { Loading, PlatformLogo } from '../../ui'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { useDeleteDraft, useDrafts } from './api'
import { RESULT_WORD, dayBR } from './constants'

const CHECK_ICON: Record<string, ReactElement> = {
  ok: <CheckCircle2 size={16} color="var(--green)" style={{ flexShrink: 0 }} />,
  error: <XCircle size={16} color="var(--red)" style={{ flexShrink: 0 }} />,
  warning: <AlertTriangle size={16} color="var(--amber)" style={{ flexShrink: 0 }} />,
  info: <Info size={16} color="var(--muted)" style={{ flexShrink: 0 }} />,
}

export function ChecksList({ checks }: { checks: ValidationCheck[] }) {
  const { companyId } = useParams()
  const order = (c: ValidationCheck) => (!c.ok && c.severity === 'error' ? 0 : !c.ok && c.severity === 'warning' ? 1 : !c.ok ? 2 : 3)
  return (
    <div className="stack" style={{ gap: 2 }}>
      {[...checks].sort((a, b) => order(a) - order(b)).map((c) => (
        <div className="row small" key={c.id} style={{ padding: '4px 0', alignItems: 'flex-start' }}>
          {CHECK_ICON[c.ok ? 'ok' : c.severity]}
          <span style={{ color: !c.ok && c.severity === 'error' ? 'var(--red)' : undefined }}>
            {c.message}
            {!c.ok && c.id.startsWith('account.') && companyId && paymentGuideFor(c.id.slice('account.'.length)) && <> <Link className="strong" style={{ color: 'var(--primary)' }} to={`/app/${companyId}/contas/pagamento?plataforma=${c.id.slice('account.'.length)}`}>Ver como regularizar</Link></>}
          </span>
        </div>
      ))}
    </div>
  )
}

export function estimateText(est: EstimateResponse | undefined, bp: BlueprintDto) {
  if (!est || est.basis === 'none' || est.high <= 0) return null
  const [one, many] = RESULT_WORD[bp.goal]
  const word = est.high === 1 ? one : many
  return `${num(est.low)} a ${num(est.high)} ${word}`
}

export function ReviewStep({ bp, onEdit, onRename, onCommit, validation, validating, validationError, onRetryValidation, estimate, canPublish }: {
  bp: BlueprintDto
  onEdit: (step: number) => void
  onRename: (name: string) => void
  /** grava a alteração do nome (ao sair do campo) */
  onCommit: () => void
  validation: ValidateResponse | undefined
  validating: boolean
  validationError: unknown
  onRetryValidation: () => void
  estimate: EstimateResponse | undefined
  canPublish: boolean
}) {
  const a = bp.audience
  const where = validation?.platforms.length
    ? validation.platforms.map((p) => `${PLATFORM_LABEL[p]}${validation.split[p] !== undefined ? ` (${brl(validation.split[p]!, 2)}/dia)` : ''}`).join(' e ')
    : bp.budget.split === 'auto' ? 'Divisão automática entre as contas conectadas' : bp.budget.split === 'meta' ? 'Instagram e Facebook' : bp.budget.split === 'tiktok' ? 'TikTok' : bp.budget.split === 'openai' ? 'ChatGPT' : 'Google'
  const rows: Array<[string, string, number]> = [
    ['Objetivo', GOAL_LABEL[bp.goal], 0],
    ['Público', `${a.ageMin} a ${a.ageMax} anos, ${a.nationwide ? 'Brasil todo' : `até ${a.radiusKm} km`}${a.interests.length ? ` · ${a.interests.join(', ')}` : ''}`, 1],
    ['Anúncio', bp.creative.headline || '(sem título)', 2],
    ['Investimento', `${brl(bp.budget.dailyBudget)}/dia · ${bp.budget.endDate ? `até ${dayBR(bp.budget.endDate)}` : 'sem data para acabar'}`, 3],
    ['Onde', where, 3],
  ]
  const est = estimateText(estimate, bp)
  return (
    <div className="stack" style={{ gap: 18 }}>
      <div>
        <h2>Tudo certo?</h2>
        <p className="muted mt-8">Confira o resumo. Você pode pausar ou mudar qualquer coisa depois.</p>
      </div>
      <div className="field">
        <label htmlFor="campaign-name">Nome da campanha (só você vê)</label>
        <input id="campaign-name" className="input" value={bp.name} maxLength={120} onChange={(e) => onRename(e.target.value)} onBlur={onCommit} onKeyDown={(e) => { if (e.key === 'Enter') onCommit() }} />
      </div>
      <div className="list">
        {rows.map(([k, v, s]) => (
          <div className="list-item" key={k}>
            <div style={{ width: 100, flexShrink: 0 }} className="muted">{k}</div>
            <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }} className="strong">{v}</div>
            <button type="button" className="btn ghost sm" onClick={() => onEdit(s)}>Alterar</button>
          </div>
        ))}
      </div>
      <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
        <div className="small strong muted" style={{ marginBottom: 10 }}>VERIFICAÇÕES AUTOMÁTICAS</div>
        {validating && !validation && <Loading lines={3} />}
        {!!validationError && !validation && (
          <div className="form-error">{errorMessage(validationError)} <button type="button" className="btn ghost sm" onClick={onRetryValidation}>Tentar de novo</button></div>
        )}
        {validation && <ChecksList checks={validation.checks} />}
        {validation && !validation.ok && <p className="small mt-8" style={{ color: 'var(--red)' }}>Corrija os itens em vermelho para {canPublish ? 'publicar' : 'deixar pronto para publicar'}.</p>}
      </div>
      {est && (
        <div className="callout green">
          <Sparkles size={18} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>Estimativa: <b>{est}</b> {bp.budget.endDate ? `até ${dayBR(bp.budget.endDate)}` : 'por mês'}{estimate?.basis === 'history' ? ', com base nas suas campanhas atuais' : ', com base em negócios parecidos'}.</span>
        </div>
      )}
      {!canPublish && (
        <div className="callout amber">
          <Info size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>Você pode montar e salvar o rascunho. Quem publica é o gestor da empresa.</span>
        </div>
      )}
    </div>
  )
}

const STATE_TEXT: Record<PlatformPublishState['status'], string> = {
  pending: 'Na fila',
  creating: 'Criando a campanha (pausada)…',
  created: 'Criada e pausada',
  activating: 'Ativando…',
  active: 'No ar (em revisão pela plataforma)',
  failed: 'Não deu certo',
}

function PlatformRow({ s }: { s: PlatformPublishState }) {
  const busy = s.status === 'creating' || s.status === 'activating' || s.status === 'pending'
  return (
    <div className="list-item">
      <PlatformLogo platform={s.platform === 'google_ads' ? 'google' : s.platform} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="strong">{PLATFORM_LABEL[s.platform]} · {brl(s.dailyBudget, 2)}/dia</div>
        <div className="small muted">{STATE_TEXT[s.status]}</div>
        {s.error && <div className="small" style={{ color: 'var(--red)', overflowWrap: 'anywhere' }}>{s.error}</div>}
      </div>
      {busy ? <RefreshCw size={16} className="spin" /> : s.status === 'failed' ? <XCircle size={18} color="var(--red)" /> : <CheckCircle2 size={18} color="var(--green)" />}
    </div>
  )
}

export function PublishStatus({ draft, companyId, canPublish, retrying, onRetry, onReview }: {
  draft: DraftDto
  companyId: string
  canPublish: boolean
  retrying: boolean
  onRetry: () => void
  onReview: () => void
}) {
  const r = draft.publishResult
  const platforms = (Object.values(r?.platforms ?? {}) as PlatformPublishState[]).sort((a, b) => (a.platform === 'meta' ? -1 : b.platform === 'meta' ? 1 : 0))

  if (draft.status === 'published') {
    return (
      <div className="card" style={{ maxWidth: 640, margin: '40px auto', textAlign: 'center', padding: 36 }}>
        <span className="ic green" style={{ width: 56, height: 56, margin: '0 auto', borderRadius: 16 }}><CheckCircle2 size={30} /></span>
        <h1 className="mt-16">Campanha enviada!</h1>
        <p className="muted mt-8">Instagram, Facebook, Google, TikTok e ChatGPT revisam todo anúncio novo. Isso costuma levar poucas horas, e no máximo 24h. Avisamos você quando estiver no ar.</p>
        {platforms.length > 0 && <div className="list mt-16" style={{ textAlign: 'left' }}>{platforms.map((s) => <PlatformRow key={s.platform} s={s} />)}</div>}
        <div className="small strong muted mt-24" style={{ textAlign: 'left' }}>O QUE ACONTECE AGORA</div>
        <div className="list mt-8" style={{ textAlign: 'left' }}>
          {[
            ['Agora', 'Anúncio em revisão pelas plataformas.'],
            ['Primeiros 3 a 5 dias', 'Fase de aprendizado: os resultados oscilam enquanto as plataformas descobrem quem responde melhor. É normal.'],
            ['Depois de 7 dias', 'Você recebe a primeira análise e sugestões de ajuste.'],
          ].map(([when, what]) => (
            <div className="list-item" key={when}>
              <span className="ic purple" style={{ width: 28, height: 28 }}><Check size={14} /></span>
              <div><div className="strong">{when}</div><div className="small muted">{what}</div></div>
            </div>
          ))}
        </div>
        <div className="row wrap mt-24" style={{ justifyContent: 'center' }}>
          <Link className="btn" to={`/app/${companyId}/campanhas`}>Ver campanhas</Link>
          <Link className="btn primary" to={`/app/${companyId}/inicio`}>Voltar ao início</Link>
        </div>
      </div>
    )
  }

  if (draft.status === 'failed') {
    return (
      <div className="card" style={{ maxWidth: 640, margin: '40px auto', padding: 28 }}>
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <span className="ic red"><XCircle size={20} /></span>
          <div>
            <h2>Não conseguimos publicar tudo</h2>
            <p className="muted mt-8">{r?.error ?? 'Algo deu errado na publicação.'} O que já foi criado ficou <b>pausado</b>, sem gastar nada. Ao tentar de novo, reaproveitamos o que já existe.</p>
          </div>
        </div>
        {platforms.length > 0 && <div className="list mt-16">{platforms.map((s) => <PlatformRow key={s.platform} s={s} />)}</div>}
        <div className="row wrap mt-24" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onReview}>Revisar a campanha</button>
          {canPublish && <button type="button" className="btn primary" onClick={onRetry} disabled={retrying}>{retrying ? <RefreshCw size={16} className="spin" /> : null} Tentar de novo</button>}
        </div>
      </div>
    )
  }

  // publishing (sem sinal de vida há 10 min = travada; o servidor também libera a nova tentativa)
  const stuck = Date.now() - new Date(draft.updatedAt).getTime() > 10 * 60_000
  return (
    <div className="card" style={{ maxWidth: 640, margin: '40px auto', padding: 28 }} aria-live="polite">
      <div className="row" style={{ gap: 12 }}>
        <span className="ic purple"><RefreshCw size={20} className="spin" /></span>
        <div>
          <h2>Publicando sua campanha…</h2>
          <p className="muted mt-8">Criamos tudo pausado e só ligamos quando estiver pronto nas {platforms.length > 1 ? 'duas plataformas' : 'plataformas'}. Pode sair desta tela: avisamos quando terminar.</p>
        </div>
      </div>
      <div className="list mt-16">
        {platforms.length ? platforms.map((s) => <PlatformRow key={s.platform} s={s} />) : <div className="list-item"><Clock size={16} /> <span className="small muted">Preparando…</span></div>}
      </div>
      {stuck && (
        <div className="mt-16">
          <p className="small muted">Está demorando mais que o normal. O que já foi criado fica pausado e é reaproveitado na nova tentativa.</p>
          {canPublish && <button type="button" className="btn primary mt-8" onClick={onRetry} disabled={retrying}>{retrying ? <RefreshCw size={16} className="spin" /> : null} Tentar de novo</button>}
        </div>
      )}
    </div>
  )
}

const STATUS_BADGE: Record<DraftDto['status'], [string, string]> = {
  draft: ['purple', 'Rascunho'],
  publishing: ['blue', 'Publicando'],
  published: ['green', 'Publicada'],
  failed: ['red', 'Falhou'],
}
const SOURCE_TEXT: Record<DraftDto['source'], string> = { user: '', autopilot: 'Sugerido pelo piloto', idea: 'Ideia da IA', assistant: 'Criado pelo assistente' }

export function DraftsList({ companyId, canEdit }: { companyId: string; canEdit: boolean }) {
  const q = useDrafts(companyId)
  const del = useDeleteDraft(companyId)
  const toast = useToast()
  const confirm = useConfirm()
  const items = (q.data?.items ?? []).filter((d) => d.status !== 'published')
  if (q.isLoading) return <div className="card"><Loading lines={2} /></div>
  if (q.isError) return <div className="form-error">Não conseguimos carregar seus rascunhos. <button type="button" className="btn ghost sm" onClick={() => q.refetch()}>Tentar de novo</button></div>
  if (!items.length) return null
  return (
    <details className="card" style={{ marginBottom: 20 }} open={items.some((d) => d.source !== 'user')}>
      <summary className="strong" style={{ cursor: 'pointer' }}>Rascunhos salvos ({items.length})</summary>
      <div className="list mt-12">
        {items.map((d) => (
          <div className="list-item" key={d.id} style={{ alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="strong" style={{ overflowWrap: 'anywhere' }}>{d.name}</div>
              <div className="small muted">
                {GOAL_LABEL[d.blueprint.goal]} · {brl(d.blueprint.budget.dailyBudget)}/dia · atualizado em {dateBR(d.updatedAt)}{SOURCE_TEXT[d.source] ? ` · ${SOURCE_TEXT[d.source]}` : ''}
              </div>
            </div>
            <span className={`badge ${STATUS_BADGE[d.status][0]}`}>{STATUS_BADGE[d.status][1]}</span>
            <Link className="btn sm" to={`/app/${companyId}/criar/${d.id}`}>Continuar</Link>
            {canEdit && d.status !== 'publishing' && (
              <button type="button" className="btn ghost sm" aria-label={`Apagar rascunho ${d.name}`} disabled={del.isPending}
                onClick={async () => { if (await confirm({ title: 'Apagar este rascunho?', confirmLabel: 'Apagar', danger: true })) del.mutate(d.id, { onSuccess: () => toast('Rascunho apagado.'), onError: (e) => toast(errorMessage(e)) }) }}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </details>
  )
}

